import React from 'react';
import { Box, Typography, Grid, Button, CircularProgress, Alert, Card, CardContent, IconButton, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import BannerSlider from '../components/BannerSlider';
import WeatherCard from '../components/weather/WeatherCard';
import RecentProjects from '../components/projects/RecentProjects';
import { useAuth } from '../context/AuthContext';
import './HomePage.css';
import BusinessIcon from '@mui/icons-material/Business';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AssessmentIcon from '@mui/icons-material/Assessment';

const HomePage = () => {
  const { user, hasPermission, isAuthenticated, loading } = useAuth();

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="home-container">
      {/* Welcome Message */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" className="ranko-title" gutterBottom>
          Ranko Tracker
        </Typography>
        <Typography variant="body1" className="ranko-subtitle">
          Advanced Agricultural Management System
        </Typography>
      </Box>
      
      {/* Banner Slider */}
      <BannerSlider />
      
      {/* Not logged in state */}
      {!isAuthenticated && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Please log in to access all features
          </Alert>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button 
                variant="contained" 
                color="primary" 
                component={Link} 
                to="/login"
                sx={{ fontWeight: 'bold', textTransform: 'none' }}
              >
                Worker Login
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                color="primary" 
                component={Link} 
                to="/customer-login"
                sx={{ fontWeight: 'bold', textTransform: 'none' }}
                startIcon={<BusinessIcon />}
              >
                Client Login
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Weather Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom className="section-title">
          Weather Information
        </Typography>
        <WeatherCard />
      </Box>
      
      {/* Circle Menu Card */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom className="section-title">
          Quick Access
        </Typography>
        <Card className="circle-menu-card">
          <CardContent>
            <Grid 
              container 
              justifyContent="center" 
              spacing={{ xs: 1, sm: 3 }}
              direction={{ xs: 'row', sm: 'row' }}
              wrap="nowrap"
            >
              <Grid item>
                <Box className="circle-menu-item">
                  <Tooltip title="News">
                    <IconButton 
                      component={Link} 
                      to="/news" 
                      className="circle-icon"
                      sx={{ 
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.2)' } 
                      }}
                    >
                      <NewspaperIcon fontSize="large" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>News</Typography>
                </Box>
              </Grid>
              
              <Grid item>
                <Box className="circle-menu-item">
                  <Tooltip title="Store">
                    <IconButton 
                      component={Link} 
                      to="/marketplace" 
                      className="circle-icon"
                      sx={{ 
                        backgroundColor: 'rgba(46, 125, 50, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.2)' } 
                      }}
                    >
                      <StorefrontIcon fontSize="large" color="success" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Store</Typography>
                </Box>
              </Grid>
              
              <Grid item>
                <Box className="circle-menu-item">
                  <Tooltip title="Analytics">
                    <IconButton 
                      component={Link} 
                      to="/dashboard" 
                      className="circle-icon"
                      sx={{ 
                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.2)' } 
                      }}
                    >
                      <AssessmentIcon fontSize="large" color="error" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="body2" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>Analytics</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      
      {/* Recent Projects (authenticated only) */}
      {isAuthenticated && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom className="section-title">
            Recent Projects
          </Typography>
          <RecentProjects />
        </Box>
      )}
    </Box>
  );
};

export default HomePage; 